import { Check, Info, Minus } from "@icons";
import { useInfoToggleName } from "@src/hooks/use-info-toggle-labels";
import classNames from "classnames";
import { ReactNode, Ref, useEffect, useId, useRef, useState } from "react";

import { FieldSize } from "../_shared/field";
import FieldMessage from "../_shared/field-message";

export interface CheckboxProps {
  label?: ReactNode;
  description?: ReactNode;
  info?: ReactNode;
  infoOpen?: boolean;
  ariaLabel?: string;
  required?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  focused?: boolean;
  disabled?: boolean;
  error?: string;
  size?: FieldSize;
  card?: boolean;
  name?: string;
  id?: string;
  onChange?: (checked: boolean) => void;
  onBlur?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  labelledByAncestor?: boolean;
}

export function Checkbox({
  label,
  description,
  info,
  infoOpen = false,
  ariaLabel,
  required = false,
  checked = false,
  indeterminate = false,
  focused = false,
  disabled = false,
  error,
  size = "md",
  card = false,
  name,
  id,
  onChange,
  onBlur,
  inputRef,
  labelledByAncestor = false,
}: CheckboxProps) {
  const infoToggleName = useInfoToggleName(label);
  const [showInfo, setShowInfo] = useState(infoOpen);
  const innerRef = useRef<HTMLInputElement | null>(null);
  const reactId = useId();
  const inputId = id ?? name ?? reactId;
  const labelId = `${inputId}-label`;
  const descriptionId = `${inputId}-description`;
  const errorId = `${inputId}-error`;
  const Wrapper = labelledByAncestor ? "span" : "div";
  const Clickable = labelledByAncestor ? "span" : "label";

  const hasError = !!error;
  const filled = checked || indeterminate;
  const describedBy =
    [description && descriptionId, hasError && errorId]
      .filter(Boolean)
      .join(" ") || undefined;

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate, checked]);

  function assignRef(node: HTMLInputElement | null) {
    innerRef.current = node;
    if (typeof inputRef === "function") inputRef(node);
    else if (inputRef)
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        node;
  }

  return (
    <Wrapper className="block w-full font-sans">
      <Wrapper className="flex items-center gap-target-pitch-gap">
        <Clickable
          htmlFor={labelledByAncestor ? undefined : inputId}
          className={classNames(
            "group relative flex",
            description ? "items-start" : "items-center",
            disabled ? "cursor-not-allowed" : "cursor-pointer",
            card
              ? classNames(
                  "flex-1 gap-3 w-auto px-[15px] py-[13px] rounded-lg border transition-[border-color,background,box-shadow]",
                  {
                    "border-primary bg-tint shadow-md": checked && !disabled,
                    "border-default bg-page hover:border-primary hover:bg-surface":
                      !checked && !disabled,
                    "border-default bg-page": disabled,
                  },
                )
              : classNames(
                  "min-h-target gap-2.5 w-fit",
                  !label && !description && "min-w-target justify-center",
                ),
          )}
        >
          <input
            ref={assignRef}
            id={inputId}
            name={name}
            type="checkbox"
            aria-label={ariaLabel}
            aria-labelledby={label && !ariaLabel ? labelId : undefined}
            aria-describedby={describedBy}
            aria-invalid={hasError || undefined}
            aria-required={required || undefined}
            className="peer absolute w-px h-px m-0 p-0 opacity-0 pointer-events-none"
            checked={checked}
            disabled={disabled}
            onChange={(event) => {
              if (!disabled) onChange?.(event.target.checked);
            }}
            onBlur={onBlur}
          />

          <span
            aria-hidden="true"
            className={classNames(
              "grid place-items-center flex-shrink-0 rounded-sm border-[1.5px] transition-[background,border-color,box-shadow]",
              size === "sm"
                ? "w-4 h-4"
                : size === "lg"
                  ? "w-5 h-5"
                  : "w-[18px] h-[18px]",
              description && "mt-px",
              {
                "bg-surface border-default text-muted": disabled && !filled,
                "bg-raised border-default text-default": disabled && filled,
                "bg-primary border-primary text-on-primary group-hover:bg-primary-hover group-hover:border-primary":
                  !disabled && filled,
                "bg-page border-default text-default group-hover:border-primary":
                  !disabled && !filled && !hasError,
                "bg-page border-danger text-default":
                  !disabled && !filled && hasError,
              },
              hasError
                ? "peer-focus-visible:ring-2 peer-focus-visible:ring-danger"
                : "peer-focus-visible:border-primary peer-focus-visible:ring-2 peer-focus-visible:ring-focus",
              focused &&
                (hasError
                  ? "ring-2 ring-danger"
                  : "border-primary ring-2 ring-focus"),
            )}
          >
            <span
              className={classNames(
                "col-start-1 row-start-1 flex transition-[opacity,transform]",
                checked && !indeterminate
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-[.6]",
              )}
            >
              <Check size={size === "sm" ? 12 : 13} strokeWidth={3} />
            </span>
            <span
              className={classNames(
                "col-start-1 row-start-1 flex transition-[opacity,transform]",
                indeterminate
                  ? "opacity-100 scale-100"
                  : "opacity-0 scale-[.6]",
              )}
            >
              <Minus size={size === "sm" ? 12 : 14} strokeWidth={3} />
            </span>
          </span>

          {(label || description) && (
            <span className="flex flex-col gap-0.5 min-w-0">
              {label && (
                <span
                  className={classNames(
                    "inline-flex items-center gap-1.5 font-medium leading-[1.35]",
                    size === "sm" ? "text-[0.8125rem]" : "text-sm",
                    disabled ? "text-muted" : "text-default",
                  )}
                >
                  <span id={labelId} className="contents">
                    {label}
                  </span>
                  {required && <span className="text-danger">*</span>}
                </span>
              )}
              {description && (
                <span
                  id={descriptionId}
                  className={classNames(
                    "text-[0.78125rem] leading-[1.45]",
                    disabled ? "text-disabled" : "text-muted",
                  )}
                >
                  {description}
                </span>
              )}
            </span>
          )}
        </Clickable>
        {info && (
          <span className="relative inline-flex">
            <button
              type="button"
              aria-label={infoToggleName}
              aria-expanded={showInfo}
              onClick={(event) => {
                event.preventDefault();
                setShowInfo((open) => !open);
              }}
              className={classNames(
                "relative hit-target grid place-items-center w-[18px] h-[18px] shrink-0 rounded-full border-0 p-0 cursor-pointer transition-colors",
                showInfo
                  ? "text-primary bg-tint"
                  : "text-muted hover:text-primary hover:bg-tint",
              )}
            >
              <Info size={14} />
            </button>
            {showInfo && (
              <span
                role="tooltip"
                className="absolute top-[calc(100%+8px)] -left-2 z-30 w-[244px] px-3 py-2.5 rounded-md border border-divider bg-page shadow-lg text-[0.78125rem] font-normal leading-normal text-muted"
              >
                <span
                  aria-hidden="true"
                  className="absolute -top-[5px] left-3 w-[9px] h-[9px] rotate-45 border-l border-t border-divider bg-page"
                />
                {info}
              </span>
            )}
          </span>
        )}
      </Wrapper>

      {hasError && (
        <Wrapper id={errorId} className="block ml-7">
          <FieldMessage error={error} />
        </Wrapper>
      )}
    </Wrapper>
  );
}

export default Checkbox;
